import React, { useEffect, useState } from 'react';
import FormModal from '../../Modals/FormModal';
import InputField from '../../Forms/Input/InputField';
import SelectField from '../../Forms/Input/SelectField';
import ToggleSwitch from '../../Forms/Input/ToggleSwitch';
import clsx from 'clsx';
import { useBranch } from '../../../hooks/branches/useBranch';
import { QzTrayConfigurationForm } from '../../../types/qztray/QzTrayConfigurationTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: QzTrayConfigurationForm) => void;
  isSubmitting: boolean;
  initialData?: QzTrayConfigurationForm | null;
}

const QzTrayConfigurationAddModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  initialData,
}) => {
  const [form, setForm] = useState<QzTrayConfigurationForm>({
    host: 'localhost',
    port: 8181,
    protocol: 'ws',
    sslEnabled: false,
    timeoutMs: 5000,
    tenantId: '', // 👈 editable manualmente
    branchId: null,
  });

  const { branchDropdown, fetchBranchDropdown } = useBranch();

  useEffect(() => {
    if (isOpen) {
      fetchBranchDropdown();
      setForm(
        initialData ?? {
          host: 'localhost',
          port: 8181,
          protocol: 'ws',
          sslEnabled: false,
          timeoutMs: 5000,
          tenantId: '',
          branchId: null,
        }
      );
    }
  }, [isOpen, initialData]);

  const updateField = (field: keyof QzTrayConfigurationForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form); // 👈 el tenantId se envía al backend junto con el resto
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        initialData
          ? 'Editar Configuración QZ Tray'
          : 'Nueva Configuración QZ Tray'
      }
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="mt-4 space-y-5">
        {/* 🏬 Fila 1: Sucursal y Protocolo */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SelectField
            label="Sucursal"
            name="branchId"
            value={form.branchId ?? ''}
            onChange={(e) => updateField('branchId', Number(e.target.value))}
            options={branchDropdown.map((b: any) => ({
              label: b.name,
              value: b.id,
            }))}
            placeholder="Seleccione una sucursal"
            iconKey="building"
            required
          />

          <SelectField
            label="Protocolo"
            name="protocol"
            value={form.protocol}
            onChange={(e) =>
              updateField('protocol', e.target.value as 'ws' | 'wss')
            }
            options={[
              { label: 'ws (no seguro)', value: 'ws' },
              { label: 'wss (seguro con SSL)', value: 'wss' },
            ]}
            placeholder="Seleccione protocolo"
            iconKey="shield"
            required
          />
        </div>

        {/* 🌐 Fila 2: Host y Puerto */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <InputField
            label="Host"
            name="host"
            type="text"
            value={form.host}
            onChange={(e) => updateField('host', e.target.value)}
            placeholder="Ejemplo: localhost o 192.168.1.10"
            iconKey="network"
            required
          />

          <InputField
            label="Puerto"
            name="port"
            type="number"
            value={form.port}
            onChange={(e) => updateField('port', Number(e.target.value))}
            placeholder="Ejemplo: 8181"
            iconKey="server"
            required
          />
        </div>

        {/* 🕒 Fila 3: Timeout + Tenant ID */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <InputField
            label="Tiempo de espera (ms)"
            name="timeoutMs"
            type="number"
            value={form.timeoutMs}
            onChange={(e) => updateField('timeoutMs', Number(e.target.value))}
            placeholder="Ejemplo: 5000"
            iconKey="clock"
            required
          />

          <InputField
            label="Tenant ID"
            name="tenantId"
            type="text"
            value={form.tenantId}
            onChange={(e) => updateField('tenantId', e.target.value)}
            placeholder="Identificador del cliente (tenant)"
            iconKey="idCard"
            required
          />
        </div>

        {/* 🔐 Fila 4: SSL habilitado (último) */}
        <div className="flex items-center justify-between rounded-lg border border-stroke px-4 py-2 dark:border-strokedark">
          <ToggleSwitch
            label="SSL habilitado"
            name="sslEnabled"
            checked={form.sslEnabled}
            onChange={() => updateField('sslEnabled', !form.sslEnabled)}
            activeColor="primary"
            inactiveColor="gray"
          />
        </div>

        {/* 🔘 Botón Guardar */}
        <div className="pt-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className={clsx(
              'w-full rounded px-4.5 py-2.5 font-medium text-white',
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-opacity-90'
            )}
          >
            {isSubmitting
              ? 'Guardando configuración...'
              : initialData
              ? 'Actualizar Configuración'
              : 'Guardar Configuración'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default QzTrayConfigurationAddModal;
